import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { LOCALE_LABELS, SUPPORTED_LOCALES, type Locale } from '@boletra/i18n';
import { setLocale } from '../lib/i18n.js';

const FLAG: Record<Locale, string> = {
  es: '🇲🇽',
  en: '🇺🇸',
};

interface Props {
  /**
   * "compact" — pill button suitable for the header (icon + ISO code).
   * "stacked" — full-width list for inside the mobile menu / settings.
   */
  variant?: 'compact' | 'stacked';
}

export function LanguageSwitcher({ variant = 'compact' }: Props) {
  const { i18n, t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const current = ((lang as Locale) ?? (i18n.language as Locale) ?? 'es') as Locale;

  function change(next: Locale) {
    setLocale(next);
    const newPath = location.pathname.replace(/^\/[a-z]{2}/, `/${next}`);
    navigate(newPath + location.search, { replace: true });
  }

  if (variant === 'stacked') {
    return (
      <div role="group" aria-label={t('language.select')} className="grid grid-cols-2 gap-2">
        {SUPPORTED_LOCALES.map((loc) => {
          const active = loc === current;
          return (
            <button
              key={loc}
              type="button"
              onClick={() => change(loc)}
              aria-pressed={active}
              className={[
                'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition',
                active
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-neutral-50'
                  : 'border-neutral-800 hover:border-neutral-600 text-neutral-300',
              ].join(' ')}
            >
              <span aria-hidden className="text-base leading-none">
                {FLAG[loc]}
              </span>
              <span>{LOCALE_LABELS[loc].native}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return <CompactPopover current={current} onChange={change} />;
}

function CompactPopover({
  current,
  onChange,
}: {
  current: Locale;
  onChange: (next: Locale) => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('language.select')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-neutral-800 bg-neutral-900/60 hover:border-neutral-700 text-sm transition"
      >
        <span aria-hidden className="text-base leading-none">
          {FLAG[current]}
        </span>
        <span className="font-medium uppercase tracking-wider text-xs">{current}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t('language.select')}
          className="absolute right-0 mt-2 min-w-[12rem] rounded-xl border border-neutral-800 bg-neutral-950/95 backdrop-blur shadow-xl shadow-black/40 p-1 z-50"
        >
          {SUPPORTED_LOCALES.map((loc) => {
            const active = loc === current;
            return (
              <li key={loc} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(loc);
                    setOpen(false);
                  }}
                  className={[
                    'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition text-left',
                    active
                      ? 'bg-[var(--color-accent)]/10 text-neutral-50'
                      : 'text-neutral-300 hover:bg-neutral-900',
                  ].join(' ')}
                >
                  <span aria-hidden className="text-lg leading-none">
                    {FLAG[loc]}
                  </span>
                  <span className="flex-1">{LOCALE_LABELS[loc].native}</span>
                  {active && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[var(--color-accent)]"
                      aria-hidden
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
